/** Keep rotating at the native scroll boundary, without moving or cloning UI. */
export function listenForGlobeBoundaryScroll(
  scroll: HTMLElement,
  move: (pixels: number) => void,
  enabled: () => boolean
) {
  const atBottom = () => scroll.scrollTop >= scroll.scrollHeight - scroll.clientHeight - 1;
  const consume = (pixels: number, event: Event) => {
    if (!enabled() || pixels <= 0 || !atBottom() || !event.cancelable) return;
    event.preventDefault();
    move(pixels);
  };
  const wheel = (event: WheelEvent) => {
    // Retain browser zoom, horizontal gestures and the normal route back up.
    if (event.ctrlKey || event.metaKey || Math.abs(event.deltaX) > Math.abs(event.deltaY)) return;
    const unit = event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? scroll.clientHeight : 1;
    consume(event.deltaY * unit, event);
  };
  let touchY: number | null = null;
  const touchStart = (event: TouchEvent) => {
    touchY = event.touches.length === 1 ? event.touches[0].clientY : null;
  };
  const touchMove = (event: TouchEvent) => {
    if (event.touches.length !== 1) {
      touchY = null;
      return;
    }
    const next = event.touches[0].clientY;
    if (touchY !== null) consume(touchY - next, event);
    touchY = next;
  };
  const touchEnd = () => {
    touchY = null;
  };
  const keyDown = (event: KeyboardEvent) => {
    if (event.ctrlKey || event.metaKey || event.altKey || event.shiftKey) return;
    const target = event.target;
    if (
      target instanceof Element &&
      target.closest("button, a, input, textarea, select, [role='menu'], [contenteditable='true']")
    )
      return;
    const pixels =
      event.key === "ArrowDown"
        ? 40
        : event.key === "PageDown" || event.key === " "
          ? scroll.clientHeight * 0.85
          : 0;
    consume(pixels, event);
  };
  scroll.addEventListener("wheel", wheel, { passive: false });
  scroll.addEventListener("touchstart", touchStart, { passive: true });
  scroll.addEventListener("touchmove", touchMove, { passive: false });
  scroll.addEventListener("touchend", touchEnd);
  scroll.addEventListener("touchcancel", touchEnd);
  scroll.addEventListener("keydown", keyDown);
  return () => {
    scroll.removeEventListener("wheel", wheel);
    scroll.removeEventListener("touchstart", touchStart);
    scroll.removeEventListener("touchmove", touchMove);
    scroll.removeEventListener("touchend", touchEnd);
    scroll.removeEventListener("touchcancel", touchEnd);
    scroll.removeEventListener("keydown", keyDown);
  };
}
